import { DownOutlined, EditOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Dropdown, Empty, Modal, Select, Space, Table, Tag, Tooltip, Typography, type MenuProps } from "antd";
import { type ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import {
  helpPublishBlockers,
  HELP_ARTICLE_STATUS_LABELS,
  HELP_CATEGORIES,
  HELP_CHANGE_TYPES,
  HELP_PLATFORMS,
  type HelpArticleEntity,
  type HelpArticleKind,
  type HelpArticleStatus,
  type HelpCategory,
  type HelpAnnounceOptions,
  type HelpChangeType,
  type HelpPlatform,
  type HelpRelease,
  isHelpAnnouncementLive,
} from "@fitness-app/data-models/entities/HelpArticle";
import { isAnnouncementPushable } from "@fitness-app/utils/src/help/helpAnnouncement";

import DraggableTable from "~/components/DraggableTable/DraggableTable";
import { usePermission } from "~/hooks/usePermission";
import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { byGuideOrder, byReleaseDate } from "./helpArticleForm";
import { PublishHelpArticleModal } from "./PublishHelpArticleModal";
import { helpStatsKey, useHelpArticleStats } from "./useHelpArticleStats";
import {
  useAnnounceHelpArticle,
  useChangeHelpArticleStatus,
  useDeleteHelpArticle,
  useHelpAnnouncementStats,
  useHelpArticles,
  useHelpPreviewLink,
  useReorderHelpGuides,
  useResendHelpAnnouncementPush,
} from "./useHelpCenter";

const STATUS_COLORS: Record<HelpArticleStatus, string> = {
  draft: "gold",
  verified: "blue",
  published: "green",
};

const clientWebOrigin = getClientWebApiBaseUrl();

const publicHref = (article: HelpArticleEntity) =>
  `${clientWebOrigin}${article.kind === "update" ? "/pomoc/co-nowego" : "/pomoc"}/${article.slug}`;

interface HelpArticlesTabProps {
  kind: HelpArticleKind;
}

export const HelpArticlesTab = ({ kind }: HelpArticlesTabProps) => {
  const navigate = useNavigate();
  const articlesQuery = useHelpArticles();
  const reorderGuides = useReorderHelpGuides();
  const changeStatus = useChangeHelpArticleStatus();
  const announceArticle = useAnnounceHelpArticle();
  const resendPush = useResendHelpAnnouncementPush();
  const deleteArticle = useDeleteHelpArticle();
  const previewLink = useHelpPreviewLink();
  const canPublish = usePermission(PermissionKey.HELP_CENTER_PUBLISH, PermissionScope.ALL);

  const [status, setStatus] = useState<HelpArticleStatus | null>(null);
  const [category, setCategory] = useState<HelpCategory | null>(null);
  const [platform, setPlatform] = useState<HelpPlatform | null>(null);
  const [changeType, setChangeType] = useState<HelpChangeType | null>(null);
  // Wpis czekający na decyzję o ogłoszeniu klientkom (okno publikacji).
  const [toPublish, setToPublish] = useState<HelpArticleEntity | null>(null);
  // Ogłoszenie opublikowanego wpisu — osobno od publikacji, bo materiał już
  // jest u klientek; tu chodzi tylko o popup.
  const [toAnnounce, setToAnnounce] = useState<HelpArticleEntity | null>(null);

  const announcedIds = useMemo(
    () =>
      (articlesQuery.data ?? [])
        .filter((article) => article.kind === "update" && article.announcedAt)
        .map((article) => article.id),
    [articlesQuery.data],
  );
  const announcementStats = useHelpAnnouncementStats(announcedIds);
  const readingStats = useHelpArticleStats(30);

  const all = articlesQuery.data ?? [];

  const rows = useMemo(() => {
    const ofKind = all.filter((article) => article.kind === kind);
    // Filtry stoją nad tabelą zamiast w nagłówkach kolumn — filtry antd
    // działają „wszystko albo nic” i potrafią wyciąć wiersze, których nie
    // powinny (patrz Support board).
    const filtered = ofKind.filter(
      (article) =>
        (!status || article.status === status) &&
        (!category || article.category === category) &&
        (!platform ||
          article.platforms.includes(platform) ||
          (platform === "mobile" && article.platforms.some((value) => value === "ios" || value === "android"))) &&
        (!changeType || article.changeType === changeType),
    );
    return filtered.sort(kind === "guide" ? byGuideOrder : byReleaseDate);
  }, [all, category, changeType, kind, platform, status]);

  const openArticle = (article: HelpArticleEntity | null) =>
    navigate(article ? `/help-center/${article.id}` : `/help-center/nowy?kind=${kind}`);

  /**
   * Akcje drugiego rzędu jednego wiersza. Potwierdzenia idą przez `Modal.confirm`,
   * bo `Popconfirm` w pozycji menu zamyka się razem z menu.
   */
  const moreActions = (article: HelpArticleEntity, changingStatus: boolean): NonNullable<MenuProps["items"]> => {
    const items: NonNullable<MenuProps["items"]> = [];
    const published = article.status === "published";

    if (canPublish && published && article.kind === "update") {
      items.push({
        key: "announce",
        // Wpis tylko o przeglądarce nie ma pusha - okno ogłoszenia i tak go wyszarza.
        label: article.announcedAt
          ? isAnnouncementPushable(article.platforms)
            ? "Ogłoś ponownie (okienko i push)"
            : "Ogłoś ponownie (okienko)"
          : "Ogłoś klientkom",
        onClick: () => setToAnnounce(article),
      });
    }

    // Dosłanie samego powiadomienia w tej samej rundzie ogłoszenia. Okienko
    // z nowością liczy aplikacja z ogłoszonego wpisu - push go nie wywołuje.
    if (
      canPublish &&
      published &&
      article.kind === "update" &&
      isHelpAnnouncementLive(article) &&
      isAnnouncementPushable(article.platforms)
    ) {
      items.push({
        key: "resend-push",
        label: "Doślij powiadomienie push",
        onClick: () =>
          Modal.confirm({
            title: "Dosłać powiadomienie push?",
            content:
              "Pójdzie do klientek, które w tym ogłoszeniu nie dostały jeszcze żadnego pusha. Klientki z nową wersją aplikacji dostaną zwykłe powiadomienie o nowości, a klientki ze starszą wersją prośbę o aktualizację. Okienka z nowością to nie pokazuje ponownie: klientki ze starszą wersją zobaczą je dopiero po aktualizacji. Kto już dostał push, nie dostanie drugiego.",
            okText: "Wyślij",
            cancelText: "Anuluj",
            onOk: () => resendPush.mutateAsync(article),
          }),
      });
    }

    if (canPublish && published) {
      items.push({
        key: "unpublish",
        label: "Cofnij publikację",
        disabled: changingStatus,
        onClick: () =>
          Modal.confirm({
            title: "Cofnąć publikację?",
            content: "Materiał i jego media przestaną być dostępne dla klientek od razu.",
            okText: "Cofnij",
            cancelText: "Zostaw",
            onOk: () => changeStatus.mutateAsync({ id: article.id, status: "verified", article }),
          }),
      });
    }

    // Opublikowany materiał kasuje tylko ten, kto może publikować - to ta sama
    // decyzja co cofnięcie publikacji, tylko nieodwracalna. Przy szkicu
    // wystarczy `helpCenter.manage`, bo klientki i tak go nie widzą.
    if (!published || canPublish) {
      if (items.length) items.push({ type: "divider" });
      items.push({
        key: "delete",
        label: "Usuń",
        danger: true,
        onClick: () =>
          Modal.confirm({
            title: published ? "Usunąć opublikowany materiał?" : "Usunąć materiał?",
            content: published
              ? "Zniknie z centrum pomocy od razu i nie da się tego cofnąć. Jeśli chcesz go tylko schować, użyj „Cofnij publikację”."
              : undefined,
            okText: published ? "Usuń na stałe" : "Usuń",
            okButtonProps: { danger: true },
            cancelText: "Anuluj",
            onOk: () => deleteArticle.mutateAsync(article.id),
          }),
      });
    }

    return items;
  };

  const columns: ColumnsType<HelpArticleEntity> = [
    ...(kind === "guide" ? [{ key: "sort", width: 40, title: "" }] : []),
    {
      title: "Tytuł",
      dataIndex: "title",
      // Bez minimalnej szerokości antd oddaje tytułowi tyle miejsca, ile zostanie
      // po kolumnach o sztywnej szerokości — w wąskim oknie schodzi to do jednej
      // litery na wiersz.
      width: 280,
      render: (title: string, article) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{title}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {article.slug}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (value: HelpArticleStatus) => <Tag color={STATUS_COLORS[value]}>{HELP_ARTICLE_STATUS_LABELS[value]}</Tag>,
    },
    {
      title: kind === "guide" ? "Kategoria" : "Rodzaj zmiany",
      key: "classification",
      width: 160,
      render: (_value, article) =>
        kind === "guide"
          ? HELP_CATEGORIES[article.category as HelpCategory]
          : HELP_CHANGE_TYPES[article.changeType as HelpChangeType],
    },
    {
      title: "Gdzie",
      dataIndex: "platforms",
      width: 180,
      render: (platforms: string[]) => (
        <Space size={4} wrap>
          {platforms.map((value) => (
            <Tag key={value}>{HELP_PLATFORMS[value as HelpPlatform]}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: kind === "update" ? "Wydanie" : "Zmieniono",
      key: "date",
      width: 140,
      render: (_value, article) =>
        dayjs(kind === "update" ? (article.releasedAt ?? article.updatedAt) : article.updatedAt).format("DD.MM.YYYY"),
    },
    // Zasięg ogłoszenia widać przy wpisie, nie w module kampanii — to nie jest
    // kampania i nie ma jej zaśmiecać.
    ...(kind === "update"
      ? [
          {
            title: "Ogłoszenie",
            key: "announcement",
            width: 160,
            render: (_value: unknown, article: HelpArticleEntity) => {
              if (!article.announcedAt) {
                return <Typography.Text type="secondary">—</Typography.Text>;
              }

              const stats = announcementStats.data?.[article.id];

              return (
                <Space direction="vertical" size={0}>
                  <Typography.Text style={{ fontSize: 12 }}>
                    {dayjs(article.announcedAt).format("DD.MM.YYYY")}
                    {article.announcePushSentAt ? " + push" : ""}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {stats ? `${stats.shown} zobaczyło, ${stats.clicked} weszło` : "brak wyświetleń"}
                  </Typography.Text>
                  {/* „bez pusha” to klientki ze starszą wersją, którym nie wysłano
                      prośby o aktualizację - popup zobaczą dopiero po niej.
                      Da się im to dosłać przyciskiem „Doślij push” (TRE-2259). */}
                  {article.announcePushRecipients !== null && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {[
                        `push: ${article.announcePushRecipients}`,
                        article.announcePushUpdateRecipients
                          ? `prośba o aktualizację: ${article.announcePushUpdateRecipients}`
                          : null,
                        `bez pusha: ${article.announcePushSkipped ?? 0}`,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography.Text>
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
    {
      title: "Czytelnicy (30 dni)",
      key: "readers",
      width: 150,
      render: (_value, article) => {
        // Brak danych (środowisko bez klucza PostHoga, szkic) to kreska, nie zero —
        // zero znaczy „opublikowany i nikt nie otworzył”.
        if (!readingStats.data || !article.publishedAt) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }

        const stats = readingStats.data.get(helpStatsKey(article.kind, article.slug));

        return (
          <Space direction="vertical" size={0}>
            <Typography.Text
              style={{ fontSize: 12 }}
            >{`${stats?.views ?? 0} otwarć, ${stats?.visitors ?? 0} osób`}</Typography.Text>
            {stats && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {`z aplikacji ${stats.appViews}`}
              </Typography.Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "Akcje",
      key: "actions",
      width: 220,
      render: (_value, article) => {
        const blockers = helpPublishBlockers({
          kind: article.kind as HelpArticleKind,
          platforms: article.platforms as HelpPlatform[],
          changeType: article.changeType as HelpChangeType | null,
          releasedAt: article.releasedAt,
          releases: article.releases as HelpRelease[] | null,
          content: article.content,
        });
        // Jak przy podglądzie: jedna mutacja na całą tabelę, więc kręciołek
        // zapalamy tylko w wierszu, z którym poleciała.
        const changingStatus = changeStatus.isLoading && changeStatus.variables?.id === article.id;

        return (
          <Space size={4} wrap>
            <Button size="small" icon={<EditOutlined />} onClick={() => openArticle(article)}>
              Edytuj
            </Button>
            {clientWebOrigin && (
              <Tooltip
                title={
                  article.status === "published" ? "Podgląd w client-webie" : "Link z podglądem szkicu, ważny dobę"
                }
              >
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  // Mutacja jest jedna na całą tabelę, więc samo `isLoading`
                  // zapala kręciołek w KAŻDYM wierszu. `variables` trzyma id,
                  // z którym mutacja poleciała — tylko ten wiersz ma się kręcić.
                  loading={previewLink.isLoading && previewLink.variables === article.id}
                  onClick={() => {
                    if (article.status === "published") {
                      window.open(publicHref(article), "_blank", "noopener");
                      return;
                    }

                    // Nieopublikowany materiał nie ma publicznego adresu —
                    // client-web podpisuje jednorazowy link, bo sekret nie ma
                    // czego szukać w bundle'u panelu.
                    previewLink.mutate(article.id, {
                      onSuccess: (url) => window.open(url, "_blank", "noopener"),
                    });
                  }}
                />
              </Tooltip>
            )}
            {/* Szkic: publikacja zostaje na wierzchu - to główna akcja na tym
                etapie. Przy samym `helpCenter.manage` przycisku po prostu nie
                ma, bo wyszarzony bez wyjaśnienia byłby zagadką. */}
            {canPublish && article.status !== "published" && (
              <Tooltip title={blockers.length ? blockers.join(" ") : ""}>
                <Button
                  size="small"
                  type="primary"
                  disabled={blockers.length > 0}
                  loading={changingStatus}
                  onClick={() => {
                    // Wpis „Co nowego” pyta jeszcze o ogłoszenie klientkom;
                    // instrukcja publikuje się od razu.
                    if (article.kind === "update") {
                      setToPublish(article);
                      return;
                    }

                    changeStatus.mutate({ id: article.id, status: "published", article });
                  }}
                >
                  Publikuj
                </Button>
              </Tooltip>
            )}
            {/* Reszta w menu: pięć przycisków w komórce się zawijało, a „push”
                obok „Ogłoś” sugerował, że to to samo (TRE-2259). */}
            {moreActions(article, changingStatus).length > 0 && (
              <Dropdown menu={{ items: moreActions(article, changingStatus) }} trigger={["click"]}>
                <Button
                  size="small"
                  loading={changingStatus || (resendPush.isLoading && resendPush.variables?.id === article.id)}
                >
                  Więcej <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </Space>
        );
      },
    },
  ];

  // Przeciąganie tylko na pełnej liście: w odfiltrowanym widoku numeracja
  // objęłaby wyłącznie widoczne wiersze i przestawiła resztę po cichu.
  const canReorder = kind === "guide" && !status && !category && !platform;

  const onUpdateListOrder = (_itemId: string, destination: number, source: number) => {
    const reordered = [...rows];
    const [moved] = reordered.splice(source, 1);
    if (!moved) {
      return;
    }
    reordered.splice(destination, 0, moved);
    reorderGuides.mutate(reordered.map((article) => article.id));
  };

  const tableProps = {
    rowKey: "id",
    loading: articlesQuery.isLoading,
    dataSource: rows,
    columns,
    pagination: false as const,
    // Suma szerokości kolumn. Węższe okno przewija tabelę w poziomie zamiast
    // zgniatać tytuł — a przeciąganie kolejności i tak działa w pionie.
    scroll: { x: kind === "guide" ? 1200 : 1180 },
    locale: { emptyText: <Empty description="Brak materiałów." /> },
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openArticle(null)}>
          {kind === "guide" ? "Nowa instrukcja" : "Nowy wpis"}
        </Button>
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 160 }}
          value={status}
          onChange={setStatus}
          options={Object.entries(HELP_ARTICLE_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="Kategoria"
          style={{ width: 180 }}
          value={category}
          onChange={setCategory}
          options={Object.entries(HELP_CATEGORIES).map(([value, label]) => ({ value, label }))}
        />
        <Select
          allowClear
          placeholder="Gdzie"
          style={{ width: 160 }}
          value={platform}
          onChange={setPlatform}
          options={Object.entries(HELP_PLATFORMS).map(([value, label]) => ({ value, label }))}
        />
        {kind === "update" && (
          <Select
            allowClear
            placeholder="Rodzaj zmiany"
            style={{ width: 180 }}
            value={changeType}
            onChange={setChangeType}
            options={Object.entries(HELP_CHANGE_TYPES).map(([value, label]) => ({ value, label }))}
          />
        )}
      </Space>

      {kind === "guide" && (
        <Typography.Text type="secondary">
          {canReorder
            ? "Kolejność decyduje o tym, co klientka widzi na /pomoc jako pierwsze. Przeciągnij, żeby ją zmienić."
            : "Kolejność ustawisz po wyczyszczeniu filtrów — przeciąganie w odfiltrowanej liście przestawiłoby też materiały, których nie widać."}
        </Typography.Text>
      )}
      {canReorder ? (
        <DraggableTable<HelpArticleEntity> {...tableProps} onUpdateListOrder={onUpdateListOrder} />
      ) : (
        <Table<HelpArticleEntity> {...tableProps} />
      )}

      <PublishHelpArticleModal
        article={toPublish}
        open={!!toPublish}
        loading={changeStatus.isLoading}
        onCancel={() => setToPublish(null)}
        onConfirm={(announce: HelpAnnounceOptions | null) => {
          if (!toPublish) {
            return;
          }

          changeStatus.mutate(
            { id: toPublish.id, status: "published", article: toPublish, announce },
            { onSettled: () => setToPublish(null) },
          );
        }}
      />

      <PublishHelpArticleModal
        article={toAnnounce}
        open={!!toAnnounce}
        mode="announce"
        loading={announceArticle.isLoading}
        onCancel={() => setToAnnounce(null)}
        onConfirm={(announce: HelpAnnounceOptions | null) => {
          if (!toAnnounce || !announce) {
            setToAnnounce(null);
            return;
          }

          announceArticle.mutate({ article: toAnnounce, announce }, { onSettled: () => setToAnnounce(null) });
        }}
      />
    </Space>
  );
};
